/**
 * @file RefineContentModal.tsx
 * @description Modal dialog that allows users to provide refinement instructions for generated content.
 */

import React, { useEffect, useRef, useState } from "react";

import { RefineContentPayload } from "../../types";

interface RefineContentModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  onSubmit: (payload: RefineContentPayload) => Promise<void> | void;
  onClose: () => void;
}

const defaultState: RefineContentPayload = {
  instructions: "",
  includeOriginalInputs: true,
  includeInternalLinks: true,
};

const RefineContentModal: React.FC<RefineContentModalProps> = ({
  isOpen,
  isSubmitting,
  onSubmit,
  onClose,
}) => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const instructionsRef = useRef<HTMLTextAreaElement | null>(null);
  const [formState, setFormState] = useState<RefineContentPayload>(defaultState);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    if (isOpen && !dialog.open) {
      dialog.showModal();
      setTimeout(() => instructionsRef.current?.focus(), 50);
    }

    if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setFormState(defaultState);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formState.instructions.trim()) {
      return;
    }
    await onSubmit({ ...formState, instructions: formState.instructions.trim() });
  };

  return (
    <dialog ref={dialogRef} className="p-0 modal">
      <form
        method="dialog"
        className="w-full max-w-3xl normal-case bg-white bg-none modal-box"
        onSubmit={handleSubmit}
      >
        <h3 className="mb-4 text-3xl !font-normal text-primary">
          Raffina il contenuto
        </h3>
        <p className="mb-6 text-base text-neutral">
          Specifica cosa desideri migliorare o modificare. Il sistema applicherà le istruzioni solo alle parti necessarie, mantenendo intatta la struttura del testo.
        </p>
        <label className="flex flex-col gap-2 mb-6">
          <span className="text-base font-medium text-primary">
            Istruzioni per l&apos;LLM
          </span>
          <textarea
            ref={instructionsRef}
            className="text-left rounded-2xl border-gray-300 focus:border-gray-300 focus:outline-gray-300 textarea textarea-bordered textarea-primary placeholder:normal-case"
            rows={6}
            placeholder="Esempio: Migliora il primo paragrafo rendendolo più concreto e aggiungi un richiamo alle linee guida del brand."
            value={formState.instructions}
            onChange={(event) =>
              setFormState((prev) => ({
                ...prev,
                instructions: event.target.value,
              }))
            }
            required
          />
        </label>
        <div className="flex flex-col gap-4 mb-8 lg:flex-row">
          <label className="flex gap-3 items-start cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 checkbox checkbox-primary"
              checked={formState.includeOriginalInputs}
              onChange={(event) =>
                setFormState((prev) => ({
                  ...prev,
                  includeOriginalInputs: event.target.checked,
                }))
              }
            />
            <span className="text-base text-neutral">
              Invia anche i dettagli originari di generazione (keyword, tipo contenuto, cliente) per facilitare il contesto.
            </span>
          </label>
          <label className="flex gap-3 items-start cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 checkbox checkbox-primary"
              checked={formState.includeInternalLinks}
              onChange={(event) =>
                setFormState((prev) => ({
                  ...prev,
                  includeInternalLinks: event.target.checked,
                }))
              }
            />
            <span className="text-base text-neutral">
              Invia l&apos;elenco degli URL interni per preservare il linking nel contenuto raffinato.
            </span>
          </label>
        </div>
        <div className="flex gap-3 justify-center">
          <button
            type="button"
            className="px-6 text-base rounded-full btn btn-outline btn-primary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Annulla
          </button>
          <button
            type="submit"
            className="px-6 !my-0 text-base border-none rounded-full btn btn-primary normal-case !text-primary-content"
            disabled={isSubmitting || !formState.instructions.trim()}
          >
            {isSubmitting ? "Raffinamento in corso..." : "Applica miglioramenti"}
          </button>
        </div>
      </form>
      <form method="dialog" className="modal-backdrop" onSubmit={handleClose}>
        <button type="submit">close</button>
      </form>
    </dialog>
  );
};

export default RefineContentModal;


